import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useEffect, useRef } from "react";
import { useGetChatSessionId } from "state/queries/useGetChatSessionId";
import {
  chatHistoryAtom,
  chatIdAtom,
  chatSessionCompanyKeyAtom,
  selectedCompanyState,
} from "store/UIHrPage";
import { useAllUserDocumentsForChatbot } from "./useAllUserDocumentsForChatbot";

export function useChatSessionId() {
  const allDocuments = useAllUserDocumentsForChatbot();
  const selectedCompany = useAtomValue(selectedCompanyState);
  const [chatId, setChatId] = useAtom(chatIdAtom);
  const setChatHistory = useSetAtom(chatHistoryAtom);
  const [sessionCompanyKey, setSessionCompanyKey] = useAtom(
    chatSessionCompanyKeyAtom
  );

  const { mutate: getChatId, isLoading } = useGetChatSessionId();

  // Retry guard for a failed request - reset on mount so leaving and
  // reopening the page gives the session another chance.
  const attemptedCompanyKeyRef = useRef<string | null>(null);

  const companyKey = selectedCompany
    ? `${selectedCompany.countryCode}-${selectedCompany.clientInternalNumberGos}`
    : null;

  useEffect(() => {
    if (isLoading || !allDocuments || !companyKey) return;

    const isSameCompany = companyKey === sessionCompanyKey;

    // Session is already running (or has just failed) for this company - keep
    // the conversation instead of starting over on every remount.
    if (
      isSameCompany &&
      (chatId || attemptedCompanyKeyRef.current === companyKey)
    ) {
      return;
    }

    // Only a real company switch invalidates the conversation.
    if (!isSameCompany) {
      setChatHistory([]);
    }

    attemptedCompanyKeyRef.current = companyKey;
    setSessionCompanyKey(companyKey);
    setChatId(null);

    getChatId(allDocuments, {
      onSuccess: (id) => {
        setChatId(id);
      },
    });
  }, [allDocuments, companyKey, sessionCompanyKey, chatId, isLoading]);
}
